import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquareWarning, Loader2, Plus, X, Send, Link2, Paperclip,
  CheckCircle2, ThumbsDown, FileText,
} from "lucide-react";

function fileUrl(path) {
  const token = localStorage.getItem("rp_token");
  return `${API}/files/${path}${token ? `?auth=${token}` : ""}`;
}

function ReviewCard({ item, onSubmitted }) {
  const [answer, setAnswer] = useState("");
  const [links, setLinks] = useState([""]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/me/results/media", fd);
      setFiles((prev) => [...prev, { path: data.path, filename: data.filename || f.name }]);
    } catch (err) {
      toast.error("Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    if (!answer.trim()) { toast.error("Please write the answer the coach should have given"); return; }
    setBusy(true);
    try {
      await api.post(`/reviewer/coach-feedback/${item.id}/respond`, {
        answer, links: links.filter((l) => l.trim()), files,
      });
      toast.success("Answer submitted — thank you!");
      setAnswer(""); setLinks([""]); setFiles([]);
      onSubmitted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit");
    } finally { setBusy(false); }
  };

  const responses = item.expert_responses || [];

  return (
    <div className="rounded-xl border border-[#E4E7EB] bg-white p-5" data-testid={`review-item-${item.id}`}>
      <div className="flex items-center gap-2 text-[11px] text-slate-400 mb-2">
        <ThumbsDown className="w-3.5 h-3.5 text-[#C8000F]" />
        {item.competitor_name || item.email || "Athlete"} · {item.created_at ? new Date(item.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
        {item.resolved && <span className="ml-1 text-[#3B6B1E] font-semibold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Resolved</span>}
      </div>

      <div className="text-sm font-semibold text-[#9E0008] mb-3">“{item.note}”</div>

      {item.question && (
        <div className="rounded-lg bg-[#FAFBFC] border border-[#EEF0F2] p-3 mb-2">
          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Athlete asked</div>
          <div className="text-sm text-[#14181F]">{item.question}</div>
        </div>
      )}
      {item.answer && (
        <div className="rounded-lg bg-[#FAFBFC] border border-[#EEF0F2] p-3 mb-4">
          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Coach answered (flagged)</div>
          <div className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-[12]">{item.answer}</div>
        </div>
      )}

      {/* Existing expert responses */}
      {responses.length > 0 && (
        <div className="space-y-2 mb-4">
          {responses.map((r, i) => (
            <div key={i} className="rounded-lg border-2 border-[#3B6B1E]/30 bg-[#F3F8EF] p-3" data-testid={`expert-response-${item.id}-${i}`}>
              <div className="text-[11px] font-bold text-[#3B6B1E] mb-1">{r.reviewer_name} · {r.created_at ? new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</div>
              <div className="text-sm text-[#14181F] whitespace-pre-wrap">{r.answer}</div>
              {(r.links || []).length > 0 && (
                <div className="mt-2 space-y-1">
                  {r.links.map((l, li) => (
                    <a key={li} href={l} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-[#1D4ED8] hover:underline break-all"><Link2 className="w-3 h-3 shrink-0" />{l}</a>
                  ))}
                </div>
              )}
              {(r.files || []).length > 0 && (
                <div className="mt-2 space-y-1">
                  {r.files.map((f, fi) => (
                    <a key={fi} href={fileUrl(f.path)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-[#1D4ED8] hover:underline break-all"><FileText className="w-3 h-3 shrink-0" />{f.filename}</a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Response form */}
      <div className="border-t border-[#EEF0F2] pt-3">
        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">{responses.length ? "Add another answer / note" : "The answer the coach SHOULD have given"}</div>
        <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={4} data-testid={`review-answer-${item.id}`}
          placeholder="Write the correct, accurate answer here…"
          className="w-full rounded-lg border border-[#D7DBE0] px-3 py-2 text-sm focus:outline-none focus:border-[#C8000F] mb-2" />

        <div className="space-y-1.5 mb-2">
          {links.map((l, i) => (
            <div key={i} className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-slate-400 shrink-0" />
              <input value={l} onChange={(e) => setLinks((p) => p.map((x, xi) => (xi === i ? e.target.value : x)))}
                placeholder="Reference link (URL) the AI should look at" data-testid={`review-link-${item.id}-${i}`}
                className="flex-1 rounded-lg border border-[#D7DBE0] px-3 py-1.5 text-xs focus:outline-none focus:border-[#C8000F]" />
              {links.length > 1 && <button onClick={() => setLinks((p) => p.filter((_, xi) => xi !== i))} className="text-slate-400 hover:text-[#C8000F]"><X className="w-4 h-4" /></button>}
            </div>
          ))}
          <button onClick={() => setLinks((p) => [...p, ""])} className="flex items-center gap-1 text-xs text-slate-500 hover:text-[#C8000F]"><Plus className="w-3.5 h-3.5" /> Add link</button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          {files.map((f, i) => (
            <span key={i} className="flex items-center gap-1.5 text-xs bg-[#F1F3F5] rounded-full px-2.5 py-1">
              <FileText className="w-3 h-3" /> {f.filename}
              <button onClick={() => setFiles((p) => p.filter((_, xi) => xi !== i))}><X className="w-3 h-3 text-slate-400 hover:text-[#C8000F]" /></button>
            </span>
          ))}
          <input ref={fileRef} type="file" onChange={uploadFile} className="hidden" data-testid={`review-file-input-${item.id}`} />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`review-file-btn-${item.id}`}
            className="flex items-center gap-1.5 text-xs font-medium border border-[#D7DBE0] rounded-lg px-3 py-1.5 hover:bg-[#FAFBFC] disabled:opacity-50">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} Attach doc
          </button>
        </div>

        <button onClick={submit} disabled={busy || !answer.trim()} data-testid={`review-submit-${item.id}`}
          className="flex items-center gap-2 bg-[#3B6B1E] hover:bg-[#2f5518] text-white rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit answer
        </button>
      </div>
    </div>
  );
}

export default function ReviewQueue() {
  const [data, setData] = useState(null);
  const load = () => api.get("/reviewer/queue").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6" data-testid="review-queue-page">
      <div className="flex items-center gap-2 mb-1"><MessageSquareWarning className="w-6 h-6 text-[#C8000F]" /><h1 className="font-display text-2xl font-extrabold tracking-tight">Review Queue</h1></div>
      <p className="text-sm text-slate-500 mb-6">Athletes flagged these coach answers. Write the correct answer plus any links or docs the AI should have used. {data ? <b>{data.open_count} open</b> : null}</p>

      {!data ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
      ) : data.items.length === 0 ? (
        <div className="rounded-xl border border-[#E4E7EB] bg-white p-10 text-center text-slate-500">Nothing to review right now. 🎉</div>
      ) : (
        <div className="space-y-4">
          {data.items.map((it) => <ReviewCard key={it.id} item={it} onSubmitted={load} />)}
        </div>
      )}
    </div>
  );
}
